import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { invalidateReviewCaches } from "../utils/homeFeedCache"
import { useParams, useNavigate, useLocation } from "react-router-dom";
import FeedReviewBox from "../components/FeedReviewBox";
import AppBar from "../components/AppBar";
import ReviewCommentBox from "../components/ReviewCommentBox";
import CommentInputSection from "../components/CommentInputSection";
import { useKeyboardInset } from "../hooks/useKeyboardInset";
import TextListBottomSheet from "../components/TextListBottomSheet";
import { ReviewProps } from "../types/review";
import { ReviewComment, getCommentAuthorDisplay } from "../types/reviewComment";
import { reviewApi } from "../api/review";
import { pageCache } from '../utils/pageCache';
import { toastUtil } from "../utils/toast";
import { deleteReviewMessage } from "../utils/reviewDeleteMessage";
import { useCommentCountStore } from "../stores/commentCountStore";
import { useAuthStore } from "../stores/authStore";
import { dialogUtil } from "../utils/dialog";
import ReportModal from "../components/ReportModal";
import ConfirmDialog from "../components/ConfirmDialog";
import { userApi } from "../api/user";

const ReviewDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userId: currentUserId } = useAuthStore();
  // 스와이프로 돌아왔을 때 이전 내용을 그대로 보여준다 (깜빡임 방지)
  const cacheKey = `review-detail:${id ?? ""}`;
  const [review, setReview] = useState<ReviewProps | null>(() =>
    pageCache.get<ReviewProps>(cacheKey),
  );
  // 댓글도 같이 담아둔다. 리뷰만 캐시하면 돌아왔을 때 댓글만 사라졌다 다시 뜬다
  const commentsCacheKey = `review-comments:${id ?? ""}`;
  const [comments, setComments] = useState<ReviewComment[]>(
    () => pageCache.get<ReviewComment[]>(commentsCacheKey) ?? [],
  );
  // 캐시가 있으면 그것부터 그린다. 무조건 true로 두면 사진 화면에서
  // 스와이프로 돌아올 때마다 스피너가 한 번 깔려 깜빡인다.
  const [isLoading, setIsLoading] = useState(!pageCache.has(cacheKey));
  const [commentText, setCommentText] = useState("");
  const [replyingToCommentId, setReplyingToCommentId] = useState<string | null>(
    null,
  );
  const [editingComment, setEditingComment] = useState<{
    commentId: string;
    text: string;
  } | null>(null);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);

  /*
   * 댓글 입력칸으로 커서를 보내는 신호.
   *
   * 피드에서 댓글 버튼을 눌러 넘어오면 state에 표시가 실려 온다. 이 화면의
   * 댓글 버튼을 눌렀을 때도 같은 신호를 쓴다. 숫자를 올리는 이유는 두 번
   * 눌러도 매번 반응하게 하기 위해서다.
   */
  const location = useLocation();
  const [commentFocusToken, setCommentFocusToken] = useState(0);

  /*
   * 키보드가 가리는 만큼 화면 아래를 비운다.
   *
   * 앱이 창 크기를 바꾸지 않으므로(App.tsx에서 전역으로 껐다) 입력칸이
   * 저절로 올라오지 않는다. 자리는 여기서 잡는다. 앱이 알려준 키보드 높이에서
   * 창이 이미 줄어든 양을 빼므로, 혹시 창이 줄어드는 환경에서도 합이 맞는다.
   */
  const keyboard = useKeyboardInset();

  /*
   * 이 화면에 있는 동안 문서 스크롤을 잠근다.
   *
   * 키보드가 올라올 때 브라우저가 입력칸을 보여주려고 페이지를 밀어 올리는데,
   * 앱도 웹뷰를 줄이고 있어서 보정이 두 겹이 된다. 그래서 입력칸이 한 번
   * 과하게 올라갔다 내려온다. 문서가 스크롤될 수 없으면 그 밀어 올리기가
   * 일어나지 않는다. 이 화면은 원래 문서를 스크롤하지 않으므로 잃는 게 없다.
   */
  /*
   * 화면이 열리는 순간, 앞 화면이 남긴 밀림을 지운다.
   *
   * 앞 기록에서 키보드를 올리면 웹킷이 입력칸을 보여주려고 페이지를 380px
   * 밀어 둔다. 키보드를 내리지 않고 다른 기록으로 넘어가면 그 값이 남은 채로
   * 새 화면이 그려지고, 잠시 뒤 웹킷이 스스로 0으로 되돌리면서 화면이 튄다.
   * 실기기 측정에서 딱 40ms 동안 나타났다:
   *
   *   2318ms off380 top-698   ← 앞 화면이 남긴 값
   *   2360ms off0   top62     ← 웹킷이 스스로 되돌림
   *
   * 그리기 전에 먼저 지우면 그 40ms가 없어진다. useLayoutEffect를 쓰는 이유가
   * 이것이다 — 화면에 나오기 전에 실행돼야 한다.
   */
  useLayoutEffect(() => {
    if (window.scrollY !== 0) {
      window.scrollTo(0, 0);
    }
  }, []);

  /*
   * 밀림이 생기는 즉시 지운다.
   *
   * 이 화면은 문서를 스크롤하지 않는다. 높이가 고정된 칸 안에서 댓글 목록만
   * 움직인다. 그러니 문서가 밀렸다면 그건 웹킷이 입력칸을 보여주려고 한 것이고,
   * 우리에게는 필요 없는 움직임이다.
   *
   * 실기기에서 재보니 웹킷도 32ms 뒤에 스스로 0으로 되돌린다:
   *   1220ms sy380 top-698  →  1252ms sy0 top62
   * 어차피 되돌릴 값이라 곧바로 지워도 서로 부딪히지 않는다. 그 32ms가
   * 눈에는 '한 번 밀렸다 뜨는' 것으로 보인다.
   *
   * (예전에 실패했던 방식과 다르다. 그때는 키보드가 올라오는 내내 여백을
   *  계산해 넣으면서 함께 되돌리려다 웹킷과 싸웠다. 지금은 계산 없이
   *  밀린 것만 지운다.)
   */
  useEffect(() => {
    const undo = () => {
      if (window.scrollY !== 0) {
        window.scrollTo(0, 0);
      }
    };
    const viewport = window.visualViewport;
    window.addEventListener("scroll", undo, { passive: true });
    viewport?.addEventListener("scroll", undo);
    viewport?.addEventListener("resize", undo);
    return () => {
      window.removeEventListener("scroll", undo);
      viewport?.removeEventListener("scroll", undo);
      viewport?.removeEventListener("resize", undo);
    };
  }, []);

  useEffect(() => {
    document.documentElement.classList.add("lock-doc-scroll");
    return () => {
      document.documentElement.classList.remove("lock-doc-scroll");
    };
  }, []);


  /** 본문 스크롤 영역. 댓글 쓰러 왔으면 목록까지 내려준다 */
  const bodyRef = useRef<HTMLDivElement>(null);

  const focusComment = () => setCommentFocusToken((n) => n + 1);

  /*
   * 댓글 쓰러 왔으면 본문을 목록 끝까지 내려준다.
   *
   * 피드에서 넘어온 경우엔 신호가 로딩 중에 도착한다. 그때는 아직 본문이
   * 없으므로, 로딩이 끝난 뒤 다시 이 자리로 와서 내린다. 댓글은 리뷰와 함께
   * 받아오므로 로딩이 끝난 시점이면 목록도 이미 채워져 있다.
   */
  useEffect(() => {
    if (!commentFocusToken || isLoading) return;
    const el = bodyRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [commentFocusToken, isLoading]);

  useEffect(() => {
    if (!(location.state as { focusComment?: boolean } | null)?.focusComment) {
      return;
    }
    focusComment();
    /*
     * 표시를 한 번 쓰고 지운다.
     *
     * 안 지우면 이 기록이 방문 기록에 그대로 남아서, 여기서 사진 화면으로
     * 갔다가 스와이프로 돌아왔을 때 누르지도 않은 키보드가 또 올라온다.
     */
    navigate(location.pathname + location.search, {
      replace: true,
      state: null,
    });
  }, [location.state, location.pathname, location.search, navigate]);
  const [isReviewActionBottomSheetOpen, setIsReviewActionBottomSheetOpen] =
    useState(false);

  const loadReview = async () => {
    if (!id) {
      toastUtil.error("기록을 불러오는데 실패했습니다");
      navigate("/main-home");
      return;
    }

    // 캐시로 이미 그려져 있으면 로딩 화면으로 덮지 않는다
    if (!pageCache.has(cacheKey)) {
      setIsLoading(true);
    }
    try {
      const response = await reviewApi.getReview(parseInt(id));
      const foundReview = response.review;

      const convertedReview: ReviewProps = {
        id: foundReview.id,
        placeId: foundReview.placeId,
        placeAddress: foundReview.placeAddress,
        user: {
          nickname: foundReview.user.nickname,
          username: foundReview.user.username ?? undefined,
          profileImage: foundReview.user.profileImage || undefined,
        },
        restaurant: {
          name: foundReview.restaurant.name,
          rating: foundReview.restaurant.rating,
          category: foundReview.restaurant.category,
        },
        reviewText: foundReview.reviewText,
        images: foundReview.images,
        // 사진 뷰어가 첫 그림부터 비율을 알려면 작은 판이 같이 가야 한다
        imageBlurs: foundReview.imageBlurs,
        likes: foundReview.likes,
        isLiked: foundReview.isLiked,
        comments: foundReview.comments,
        date: foundReview.date,
        dateDisplay: foundReview.dateDisplay,
        visitType: foundReview.visitType,
        isPublic: foundReview.isPublic,
        visitNumber: foundReview.visitNumber,
        earlierVisits: foundReview.earlierVisits,
        isFollowing: foundReview.isFollowing,
        userId: foundReview.user.userId || undefined,
        isBookmarked: foundReview.isBookmarked || false,
        // 이 값이 빠져 있어서 FeedReviewBox의 PlaceAddButton이 항상 플러스로 떴다.
        // 이미 리뷰를 남긴 장소면 체크로 보여야 한다. (서버는 ReviewFeedResource로
        // 내려주고 있었고, 여기서 옮기지 않은 게 원인)
        hasUserReview: foundReview.hasUserReview || false,
        hideScore: foundReview.hideScore || false,
        taggedFriends: foundReview.taggedFriends?.map((f: any) => ({
          id: f.id,
          username: f.username,
          nickname: f.nickname,
          profileImage: f.profileImage || f.profile_image,
        })),
      };
      pageCache.set(cacheKey, convertedReview);
      setReview(convertedReview);

      // 코멘트 리스트 불러오기
      try {
        const commentsResponse = await reviewApi.getComments(
          parseInt(foundReview.id),
        );
        const convertedComments: ReviewComment[] =
          commentsResponse.comments.map((comment) => ({
            id: comment.id,
            user: {
              nickname: comment.user.nickname,
              username: comment.user.username ?? undefined,
              profileImage: comment.user.profileImage || undefined,
            },
            userId: comment.userId ?? undefined,
            text: comment.text,
            likes: comment.likes,
            isLiked: comment.isLiked,
            parentCommentId: comment.parentCommentId,
            mentions: comment.mentions,
          }));
        pageCache.set(commentsCacheKey, convertedComments);
        setComments(convertedComments);

        // 실제 댓글 개수로 리뷰의 comments 필드 업데이트 (모든 댓글 카운트)
        const totalCommentCount = convertedComments.length;
        // 피드가 이 값을 쓴다 — 목록 스냅샷은 갱신되지 않아 예전 숫자로 돌아간다
        if (id) useCommentCountStore.getState().setCount(id, totalCommentCount);
        setReview((prevReview) => {
          if (!prevReview) return null;
          return {
            ...prevReview,
            comments: totalCommentCount,
          };
        });
      } catch (error) {
        // 코멘트 로드 실패는 에러 표시하지 않음 (리뷰는 정상 표시)
        console.error("코멘트 로드 실패:", error);
      }
    } catch (error) {
      console.error("리뷰 로드 실패:", error);
      toastUtil.error("기록을 불러오는데 실패했습니다");
      navigate("/main-home");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadComments = async () => {
    if (!id) return;

    try {
      const commentsResponse = await reviewApi.getComments(parseInt(id));
      const convertedComments: ReviewComment[] = commentsResponse.comments.map(
        (comment) => ({
          id: comment.id,
          user: {
            nickname: comment.user.nickname,
            username: comment.user.username ?? undefined,
            profileImage: comment.user.profileImage || undefined,
          },
          userId: comment.userId ?? undefined,
          text: comment.text,
          likes: comment.likes,
          isLiked: comment.isLiked,
          parentCommentId: comment.parentCommentId,
          mentions: comment.mentions,
        }),
      );
      pageCache.set(commentsCacheKey, convertedComments);
      setComments(convertedComments);

      // 실제 댓글 개수로 리뷰의 comments 필드 업데이트 (모든 댓글 카운트)
      const totalCommentCount = convertedComments.length;
      // 피드가 이 값을 쓴다 (loadReview 쪽과 같은 이유)
      if (id) useCommentCountStore.getState().setCount(id, totalCommentCount);
      setReview((prevReview) => {
        if (!prevReview) return null;
        return {
          ...prevReview,
          comments: totalCommentCount,
        };
      });
    } catch (error) {
      console.error("코멘트 로드 실패:", error);
    }
  };

  // 댓글 좋아요 토글 결과를 comments state에 반영 (부모가 항상 최신 → stale 재시드 방지)
  const handleCommentLikeChange = (
    commentId: string,
    isLiked: boolean,
    likeCount: number,
  ) => {
    setComments((prev) =>
      prev.map((c) =>
        c.id === commentId ? { ...c, isLiked, likes: likeCount } : c,
      ),
    );
  };

  const handleCommentSubmit = async () => {
    if (!id || !commentText.trim() || isSubmittingComment) return;

    setIsSubmittingComment(true);
    try {
      if (editingComment) {
        await reviewApi.updateComment(
          parseInt(id),
          parseInt(editingComment.commentId),
          {
            commentText: commentText.trim(),
          },
        );
        setCommentText("");
        setEditingComment(null);
        await loadComments();
        toastUtil.success("댓글이 수정되었습니다");
      } else {
        await reviewApi.createComment(parseInt(id), {
          commentText: commentText.trim(),
          parentCommentId: replyingToCommentId
            ? parseInt(replyingToCommentId)
            : undefined,
        });
        setCommentText("");
        setReplyingToCommentId(null);
        // 토스트를 띄우지 않는다. loadComments()로 방금 쓴 댓글이 목록에
        // 바로 나타나므로, 그 자체가 확인이다. 화면 아래를 가리기만 했다.
        await loadComments();
      }
    } catch (error) {
      console.error(
        editingComment ? "댓글 수정 실패:" : "댓글 작성 실패:",
        error,
      );
      toastUtil.error(
        editingComment
          ? "댓글 수정 중 오류가 발생했습니다"
          : "댓글 작성 중 오류가 발생했습니다",
      );
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleReplyClick = (commentId: string) => {
    setReplyingToCommentId(commentId);
    setEditingComment(null);
  };

  const handleEditCommentClick = (commentId: string) => {
    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return;
    setEditingComment({ commentId, text: comment.text });
    setCommentText(comment.text);
    setReplyingToCommentId(null);
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setCommentText("");
  };

  const handleCommentTextChange = (text: string) => {
    setCommentText(text);
  };

  const handleCancelReply = () => {
    setReplyingToCommentId(null);
  };

  const isOwnReview = !!(
    review &&
    currentUserId &&
    review.userId === currentUserId
  );

  // 남의 기록일 때만 뜨는 신고·차단 통로
  const [isAuthorSheetOpen, setIsAuthorSheetOpen] = useState(false);
  const [isReportOpen, setIsReportOpen] = useState(false);
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);

  /**
   * 기록을 쓴 사람을 차단한다.
   *
   * 차단하면 이 기록도 더 이상 내려오지 않으므로 화면에 머물 수 없다.
   * 피드가 들고 있던 목록도 같이 버린다 — 안 버리면 홈으로 돌아갔을 때
   * 방금 차단한 사람의 기록이 스냅샷으로 되살아난다.
   */
  const handleBlockAuthor = async () => {
    if (!review?.userId || isBlocking) return;

    setIsBlocking(true);
    try {
      await userApi.blockUser(review.userId);
      invalidateReviewCaches();
      setIsBlockDialogOpen(false);
      toastUtil.success("차단되었습니다");
      navigate("/main-home");
    } catch (error) {
      console.error("차단 실패:", error);
      // 메시지는 apiClient 인터셉터가 띄운다
    } finally {
      setIsBlocking(false);
    }
  };

  const handleOpenReviewActionBottomSheet = () => {
    setIsReviewActionBottomSheetOpen(true);
  };

  const handleCloseReviewActionBottomSheet = () => {
    setIsReviewActionBottomSheetOpen(false);
  };

  const handleEditReview = () => {
    if (!review) return;
    // 어느 기록을 고칠지 이미 골라서 들어온 것이다 — 기록 화면이 다시 묻지 않게
    // id를 실어 보낸다.
    navigate(`/store-review/${review.placeId}?reviewId=${review.id}`);
  };

  const handleDeleteReview = async () => {
    if (!id || !review) return;

    const confirmed = await dialogUtil.confirm({
      message: deleteReviewMessage(review.visitNumber),
      confirmText: "삭제",
      cancelText: "취소",
    });

    if (!confirmed) return;

    try {
      await reviewApi.deleteReview(parseInt(id));
      // 지운 기록이 홈 피드·랭킹 캐시에 남아 있으면 계속 보인다. 버린다.
      invalidateReviewCaches()
      toastUtil.success("기록이 삭제되었습니다");
      navigate(-1);
    } catch (error) {
      console.error("리뷰 삭제 실패:", error);
      toastUtil.error("기록 삭제 중 오류가 발생했습니다");
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <p className="text-style-body1 text-textColorText2">로딩 중...</p>
      </div>
    );
  }

  if (!review) {
    return null;
  }

  const renderCommentList = () => {
    const MAX_DEPTH = 1;

    // 댓글의 뎁스를 계산하는 함수
    const calculateDepth = (
      commentId: string,
      comments: ReviewComment[],
      depth = 0,
    ): number => {
      const comment = comments.find((c) => c.id === commentId);
      if (!comment || !comment.parentCommentId) {
        return depth;
      }
      return calculateDepth(comment.parentCommentId, comments, depth + 1);
    };

    // 특정 댓글의 모든 하위 댓글을 재귀적으로 수집하는 함수
    const getAllDescendants = (
      commentId: string,
      comments: ReviewComment[],
    ): ReviewComment[] => {
      const descendants: ReviewComment[] = [];
      const directReplies = comments.filter(
        (c) => c.parentCommentId === commentId,
      );

      for (const reply of directReplies) {
        descendants.push(reply);
        descendants.push(...getAllDescendants(reply.id, comments));
      }

      return descendants;
    };

    // 완전히 평면화된 댓글 리스트 생성 (재귀 구조 제거)
    const flattenComments = (
      comments: ReviewComment[],
    ): Array<{
      comment: ReviewComment;
      depth: number;
    }> => {
      const result: Array<{
        comment: ReviewComment;
        depth: number;
      }> = [];

      // 최상위 댓글들 찾기
      const topLevelComments = comments.filter(
        (comment) => !comment.parentCommentId,
      );

      const processComment = (comment: ReviewComment, currentDepth: number) => {
        const commentDepth = calculateDepth(comment.id, comments);
        const actualDepth = Math.min(commentDepth, MAX_DEPTH);

        result.push({
          comment,
          depth: actualDepth,
        });

        // 현재 뎁스가 MAX_DEPTH일 때는 모든 하위 댓글을 평면화하여 같은 뎁스로 추가
        if (actualDepth === MAX_DEPTH) {
          const descendants = getAllDescendants(comment.id, comments);
          descendants.forEach((descendant) => {
            result.push({
              comment: descendant,
              depth: MAX_DEPTH,
            });
          });
        } else {
          // MAX_DEPTH 미만일 때는 직접 답글만 재귀 처리
          const directReplies = comments.filter(
            (c) => c.parentCommentId === comment.id,
          );
          directReplies.forEach((reply) => {
            processComment(reply, actualDepth + 1);
          });
        }
      };

      topLevelComments.forEach((comment) => {
        processComment(comment, 0);
      });

      return result;
    };

    const flattenedComments = flattenComments(comments);

    const commentItems = flattenedComments.map((item) => (
      <ReviewCommentBox
        key={item.comment.id}
        comment={item.comment}
        depth={item.depth}
        reviewId={id || ""}
        onReplyClick={handleReplyClick}
        onEditClick={handleEditCommentClick}
        onCommentDeleted={loadComments}
        isReviewOwner={isOwnReview}
        replyCount={getAllDescendants(item.comment.id, comments).length}
        onLikeChange={handleCommentLikeChange}
      />
    ));

    return (
      <div className="w-full flex flex-col px-20px py-16px gap-16px">
        {commentItems}
      </div>
    );
  };

  const renderBody = () => {
    return (
      <div
        ref={bodyRef}
        className="app-container flex-1 flex flex-col overflow-y-auto scrollbar-hide"
      >
        {/*
          내용이 짧아도 이 칸이 항상 조금은 굴러가게 만든다.

          왜 필요한가 — 실기기 영상에서 확인
          -------------------------------
          웹킷은 입력칸을 보여줘야 할 때 먼저 안쪽 스크롤 칸을 굴려본다.
          글이 길면 거기서 끝나지만(원라멘 본점: 정상), 짧아서 굴릴 게 없으면
          페이지 전체를 움직인다(프라이빗플레이스: 앱바가 화면 중간까지 내려감).

          그래서 같은 코드인데 어떤 기록에서는 멀쩡하고 어떤 기록에서는 밀렸다.
          1px만 넘치게 해두면 웹킷이 언제나 이 칸을 굴리고 끝낸다.
          사용자에게는 보이지 않는 양이다.
        */}
        <div className="w-full min-h-[calc(100%+1px)] flex flex-col">
        {/* 리뷰 상세 내용 */}
        <FeedReviewBox
          review={review}
          disableReviewDetailClick
          hideScore={review.hideScore || false}
          onCommentClick={focusComment}
        />

        {/* 코멘트 리스트 */}
        {renderCommentList()}
        </div>
      </div>
    );
  };

  const replyingToComment = replyingToCommentId
    ? comments.find((c) => c.id === replyingToCommentId) || null
    : null;

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{
        paddingBottom: keyboard.padding,
        // 키보드와 같은 시간 동안 움직여야 따로 노는 느낌이 안 난다.
        // 길이를 모르는 빌드에서는 0이라 즉시 적용된다.
        transition: keyboard.duration
          ? `padding-bottom ${keyboard.duration}ms cubic-bezier(0.2, 0.8, 0.3, 1)`
          : undefined,
      }}
    >

      {/* 앱바 */}
      <AppBar
        title="자세히보기"
        isBottomBorder
        rightElement={
          isOwnReview ? (
            <button
              type="button"
              onClick={handleOpenReviewActionBottomSheet}
              className="font-pretendard font-bold text-16px leading-24px text-[#6B5E46] pr-20px"
            >
              수정
            </button>
          ) : review?.userId ? (
            <button
              type="button"
              aria-label="더보기"
              onClick={() => setIsAuthorSheetOpen(true)}
              /*
                오른쪽 여백 20px — 바로 위 '수정' 버튼과 같은 값이고,
                왼쪽 뒤로가기 아이콘의 시각적 여백과도 맞는다.
                viewBox를 점에 꼭 맞춰 뒀으므로 pr 값이 곧 화면 끝까지의 거리다.
              */
              className="h-48px flex items-center justify-end pr-20px hover:opacity-70 text-[#6B5E46]"
            >
              {/* 가로로 놓인 점 세 개 */}
              <svg width="18" height="4" viewBox="0 0 18 4" fill="none">
                <circle cx="1.8" cy="2" r="1.8" fill="currentColor" />
                <circle cx="9" cy="2" r="1.8" fill="currentColor" />
                <circle cx="16.2" cy="2" r="1.8" fill="currentColor" />
              </svg>
            </button>
          ) : undefined
        }
      />

      <TextListBottomSheet
        isOpen={isAuthorSheetOpen}
        onClose={() => setIsAuthorSheetOpen(false)}
        items={[
          { text: "신고", onClick: () => setIsReportOpen(true) },
          { text: "차단", onClick: () => setIsBlockDialogOpen(true) },
        ]}
      />

      {/*
        여기서 누르는 신고는 '이 기록'이 대상이다. 프로필에서 누를 때와 다르다 —
        거기서는 사람이 문제라서 왔고, 여기서는 보고 있는 글이 문제라서 온다.
        서버도 통로가 둘로 나뉘어 있다.
      */}
      <ReportModal
        isOpen={isReportOpen}
        reviewId={id ? parseInt(id) : undefined}
        onClose={() => setIsReportOpen(false)}
      />

      <ConfirmDialog
        isOpen={isBlockDialogOpen}
        title={`${review?.user?.nickname ?? ""}님을 차단하시겠어요?`}
        message="차단 사실은 알리지 않습니다"
        confirmText={isBlocking ? "차단 중..." : "차단하기"}
        cancelText="취소"
        onCancel={() => setIsBlockDialogOpen(false)}
        onConfirm={handleBlockAuthor}
      />

      {/* 바디 */}
      {renderBody()}

      {/* 댓글 입력 섹션 */}
      <CommentInputSection
        commentText={commentText}
        focusToken={commentFocusToken}
        replyingToComment={
          replyingToComment
            ? {
                authorLabel: getCommentAuthorDisplay(replyingToComment.user),
              }
            : null
        }
        editingComment={
          editingComment ? { commentId: editingComment.commentId } : null
        }
        isSubmittingComment={isSubmittingComment}
        onCommentTextChange={handleCommentTextChange}
        onSubmit={handleCommentSubmit}
        onCancelReply={handleCancelReply}
        onCancelEdit={handleCancelEdit}
      />

      {/* 리뷰 수정/삭제 바텀 시트 */}
      <TextListBottomSheet
        isOpen={isReviewActionBottomSheetOpen}
        onClose={handleCloseReviewActionBottomSheet}
        items={[
          {
            text: "기록 수정",
            onClick: handleEditReview,
          },
          {
            text: "기록 삭제",
            onClick: handleDeleteReview,
          },
        ]}
      />
    </div>
  );
};

export default ReviewDetail;
